import instance from 'api/index';
import { getScheduleToken, getUserToken } from 'utils/localstore';
import { Request } from '../request';
import type {
    TFaxScheduleSearchParams,
    TFaxProvisionReq,
    TFaxInboxListQuery,
    TFaxSentListQuery,
    TFaxContactCreateReq,
    TFaxContactListQuery,
    TFaxCoverPageTemplateReq,
    TFaxCoverPageTemplateListQuery,
    TFaxSendParams,
    FaxNumberStatusVo,
    TFaxPortInReq,
    TFaxPortInSubmitReq,
    FileUpload,
} from 'types/eFax';

const PREFIX = '/schedule-server/fax';

/** Search available fax numbers (query: state required, areaCode optional). */
export const faxNumberSearch = (params: TFaxScheduleSearchParams) =>
    Request.get<TFaxScheduleSearchParams>({
        url: `${PREFIX}/number/search`,
        params,
    });

/** Provision / order a fax number. */
export const faxNumberProvision = (params: TFaxProvisionReq) =>
    Request.post<TFaxProvisionReq>({
        url: `${PREFIX}/number/provision`,
        params,
    });

/** Get provider active/cancelled eFax account status. */
export const faxNumberStatus = () =>
    Request.get<undefined>({
        url: `${PREFIX}/number/status`,
    }) as Promise<{ data: { code: number; data: FaxNumberStatusVo; message: string } }>;

/** Inbound fax inbox (paged). */
export const faxInboxList = (params: TFaxInboxListQuery) =>
    Request.post<TFaxInboxListQuery>({
        url: `${PREFIX}/inbox/list`,
        params,
    });

/** Outbound sent fax list (paged). */
export const faxSentList = (params: TFaxSentListQuery) =>
    Request.post<TFaxSentListQuery>({
        url: `${PREFIX}/sent/list`,
        params,
    });

/**
 * Create fax contact (Documo).
 * POST JSON body: { faxNumber?, isFavorite?, name?, notes?, tag? }.
 * Auth: `schedule-token` + `token` headers via Request (OpenAPI may list header as Token).
 * Response envelope: { code, message, data: FaxContactVo }.
 */
export const faxContactCreate = (params: TFaxContactCreateReq) =>
    Request.post<TFaxContactCreateReq>({
        url: `${PREFIX}/contact`,
        params,
        disableErrorNote: true,
    });

/** Update fax contact by id. */
export const faxContactUpdate = (id: number, params: TFaxContactCreateReq) =>
    Request.put<TFaxContactCreateReq>({
        url: `${PREFIX}/contact/${id}`,
        params,
        disableErrorNote: true,
    });

/** Delete fax contact by id. */
export const faxContactDelete = (id: number) =>
    Request.delete({
        url: `${PREFIX}/contact/${id}`,
        disableErrorNote: true,
    });

/** Paged fax contacts (filters + sort). */
export const faxContactList = (params: TFaxContactListQuery) =>
    Request.post<TFaxContactListQuery>({
        url: `${PREFIX}/contact/list`,
        params,
        disableErrorNote: true,
    });

/** Single fax contact by id (path `id`). */
export const faxContactGet = (id: number) =>
    Request.get({
        url: `${PREFIX}/contact/${id}`,
        disableErrorNote: true,
    });

/** Cancel provider eFax number. DELETE /fax/number/cancel */
export const faxNumberCancel = () =>
    Request.delete({
        url: `${PREFIX}/number/cancel`,
    });

/** Submit port-in request for an existing fax number (multipart/form-data). */
export const faxNumberPortIn = async (params: TFaxPortInReq) => {
    const formData = new FormData();
    formData.append('loaFile', params.loaFile);
    formData.append('recentBillFile', params.recentBillFile);
    if (params.faxNumber) formData.append('faxNumber', params.faxNumber);
    if (params.authorizedPerson) formData.append('authorizedPerson', params.authorizedPerson);
    if (params.billingTelephoneNumber) formData.append('billingTelephoneNumber', params.billingTelephoneNumber);
    if (params.carrierAccountNumber) formData.append('carrierAccountNumber', params.carrierAccountNumber);
    if (params.endUserName) formData.append('endUserName', params.endUserName);
    if (params.endUserCity) formData.append('endUserCity', params.endUserCity);
    if (params.endUserState) formData.append('endUserState', params.endUserState);
    if (params.endUserPostalCode) formData.append('endUserPostalCode', params.endUserPostalCode);
    if (params.pinPasscode) formData.append('pinPasscode', params.pinPasscode);
    if (params.practiceAddress) formData.append('practiceAddress', params.practiceAddress);

    const headers: Record<string, string | undefined> = {};
    const scheduleToken = getScheduleToken();
    const userToken = getUserToken();
    if (scheduleToken) headers['schedule-token'] = scheduleToken;
    if (userToken) headers.token = userToken;

    try {
        const response = await instance.post(`${PREFIX}/number/port-in`, formData, { headers });
        if (response.status >= 200 && response.status < 400) {
            return { data: response.data, error: undefined };
        }
        const errMsg = response.data?.message || `${response.status} ${response.statusText}`;
        return { success: false, error: errMsg };
    } catch (err: any) {
        const errMsg = err?.data?.message || err?.response?.data?.message || err?.message || 'Port-in request failed';
        return { success: false, error: errMsg };
    }
};

/**
 * Upload a file for fax port-in.
 * POST multipart/form-data: { file }
 * Response: R«FileUpload» (expects a fileUploadId / id to be returned).
 */
export const faxNumberPortInUpload = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    const headers: Record<string, string | undefined> = {};
    const scheduleToken = getScheduleToken();
    const userToken = getUserToken();
    if (scheduleToken) headers['schedule-token'] = scheduleToken;
    if (userToken) headers.token = userToken;
    // Unset Content-Type so the browser auto-sets multipart/form-data with the correct boundary
    headers['Content-Type'] = undefined;

    try {
        const response = await instance.post(`${PREFIX}/number/port-in/upload`, formData, { headers });
        if (response.status >= 200 && response.status < 400) {
            return { data: response.data as { code?: number; message?: string; data?: FileUpload }, error: undefined };
        }
        const errMsg = response.data?.message || `${response.status} ${response.statusText}`;
        return { success: false, error: errMsg };
    } catch (err: any) {
        const errMsg = err?.response?.data?.message || err?.message || 'Upload failed';
        return { success: false, error: errMsg };
    }
};

/** Submit port-in request for an existing fax number (application/json, using upload ids). */
export const faxNumberPortInSubmit = async (params: TFaxPortInSubmitReq) => {
    const headers: Record<string, string> = {};
    const scheduleToken = getScheduleToken();
    const userToken = getUserToken();
    if (scheduleToken) headers['schedule-token'] = scheduleToken;
    if (userToken) headers.token = userToken;

    try {
        const response = await instance.post(`${PREFIX}/number/port-in`, params, { headers });
        if (response.status >= 200 && response.status < 400) {
            return { data: response.data, error: undefined };
        }
        const errMsg = response.data?.message || `${response.status} ${response.statusText}`;
        return { success: false, error: errMsg };
    } catch (err: any) {
        const errMsg = err?.data?.message || err?.response?.data?.message || err?.message || 'Port-in request failed';
        return { success: false, error: errMsg };
    }
};

/**
 * Create cover page template.
 * POST JSON body: { documoCoverpageId?, isDefault?, note?, previewLink?, subject?, tag?, templateName? }.
 * Auth: `schedule-token` + `token` headers via Request (OpenAPI may list header as Token).
 * Response envelope: { code, message, data: FaxCoverPageTemplateVo }.
 */
export const faxCoverPageTemplateCreate = (params: TFaxCoverPageTemplateReq) =>
    Request.post<TFaxCoverPageTemplateReq>({
        url: `${PREFIX}/cover-page-template`,
        params,
        disableErrorNote: true,
    });

/** GET /fax/inbox/unread/count */
export const faxInboxUnreadCount = () =>
    Request.get<undefined>({
        url: `${PREFIX}/inbox/unread/count`,
    });

/** GET /fax/{uuid} — fax detail */
export const faxDetail = (uuid: string) =>
    Request.get({
        url: `${PREFIX}/${uuid}`,
    });

/** DELETE /fax/{uuid} — delete a fax message */
export const faxDeleteMessage = (uuid: string) =>
    Request.delete({
        url: `${PREFIX}/${uuid}`,
    });

/** GET /fax/{uuid}/download — get fax download URL */
export const faxDownload = (uuid: string) =>
    Request.get({
        url: `${PREFIX}/${uuid}/download`,
    });

/** POST /fax/{uuid}/mark-read */
export const faxMarkRead = (uuid: string) =>
    Request.post<undefined>({
        url: `${PREFIX}/${uuid}/mark-read`,
    });

/** POST /fax/{uuid}/assign-patient/{clientId} */
export const faxAssignPatient = (uuid: string, clientId: number) =>
    Request.post<undefined>({
        url: `${PREFIX}/${uuid}/assign-patient/${clientId}`,
    });

/** POST /fax/{uuid}/remove-patient */
export const faxRemovePatient = (uuid: string) =>
    Request.post<undefined>({
        url: `${PREFIX}/${uuid}/remove-patient`,
    });

/** POST /fax/{uuid}/confirm-patient */
export const faxConfirmPatient = (uuid: string) =>
    Request.post<undefined>({
        url: `${PREFIX}/${uuid}/confirm-patient`,
    });

/** POST /fax/{uuid}/cancel */
export const faxCancelMessage = (uuid: string) =>
    Request.post<undefined>({
        url: `${PREFIX}/${uuid}/cancel`,
    });

/** POST /fax/{uuid}/resend */
export const faxResend = (uuid: string) =>
    Request.post<undefined>({
        url: `${PREFIX}/${uuid}/resend`,
    });

/** POST /fax/cover-page-template/list */
export const faxCoverPageTemplateList = (params: TFaxCoverPageTemplateListQuery) =>
    Request.post<TFaxCoverPageTemplateListQuery>({
        url: `${PREFIX}/cover-page-template/list`,
        params,
        disableErrorNote: true,
    });

/** GET /fax/cover-page-template/{id} */
export const faxCoverPageTemplateGet = (id: number) =>
    Request.get({
        url: `${PREFIX}/cover-page-template/${id}`,
        disableErrorNote: true,
    });

/** PUT /fax/cover-page-template/{id} */
export const faxCoverPageTemplateUpdate = (id: number, params: TFaxCoverPageTemplateReq) =>
    Request.put<TFaxCoverPageTemplateReq>({
        url: `${PREFIX}/cover-page-template/${id}`,
        params,
        disableErrorNote: true,
    });

/** DELETE /fax/cover-page-template/{id} */
export const faxCoverPageTemplateDeleteById = (id: number) =>
    Request.delete({
        url: `${PREFIX}/cover-page-template/${id}`,
        disableErrorNote: true,
    });

/** GET /fax/cover-page-template/documo-list */
export const faxDocumoList = () =>
    Request.get<undefined>({
        url: `${PREFIX}/cover-page-template/documo-list`,
        disableErrorNote: true,
    });

/**
 * POST /fax/send — multipart/form-data:
 * - `req` part as application/json (same as curl `--form 'req=...;type=application/json'`)
 * - one or more `files` parts
 */
const buildFaxSendReqBody = (meta: Omit<TFaxSendParams, 'files'>): Record<string, unknown> => ({
    recipientFax: meta.recipientFax ?? null,
    recipientName: meta.recipientName ?? null,
    recipientEmail: meta.recipientEmail ?? null,
    contactId: meta.contactId ?? null,
    subject: meta.subject ?? null,
    notes: meta.notes ?? null,
    tag: meta.tag?.length ? meta.tag : [],
    coverPage: meta.coverPage ?? false,
    coverPageTemplateId: meta.coverPageTemplateId ?? null,
    clientId: meta.clientId ?? null,
    source: meta.source ?? 'DIRECT',
    sourceId: meta.sourceId ?? null,
    intakeBusinessType: meta.intakeBusinessType ?? null,
    documentIds: meta.documentIds != null && meta.documentIds.length > 0 ? meta.documentIds : null,
});

export const faxSend = async (params: TFaxSendParams) => {
    const { files, ...meta } = params;

    const reqJson = JSON.stringify(buildFaxSendReqBody(meta));
    const formData = new FormData();
    formData.append('req', new Blob([reqJson], { type: 'application/json' }));
    if (files?.length) {
        files.forEach((f) => formData.append('files', f));
    }

    const headers: Record<string, string | undefined> = {};
    const scheduleToken = getScheduleToken();
    const userToken = getUserToken();
    if (scheduleToken) headers['schedule-token'] = scheduleToken;
    if (userToken) headers.token = userToken;
    // Unset Content-Type so the browser auto-sets multipart/form-data with the correct boundary
    headers['Content-Type'] = undefined;

    try {
        const response = await instance.post(`${PREFIX}/send`, formData, { headers });
        if (response.status >= 200 && response.status < 400) {
            return { data: response.data, error: undefined };
        }
        const errMsg = response.data?.message || `${response.status} ${response.statusText}`;
        return { success: false, error: errMsg };
    } catch (err: any) {
        const errMsg = err?.response?.data?.message || err?.message || 'Failed to send fax';
        return { success: false, error: errMsg };
    }
};
